import type { SegmentedMeterSegment } from "./segmented-meter-segment";
import { SegmentedMeterLegendItem } from "./segmented-meter-legend-item";

/** Props Interface */
export interface SegmentedMeterProps {
	segments: SegmentedMeterSegment[];
	/** What the segments are shares of; what they leave of it stays an empty track. */
	total: number;
	/** The legend's entry for the part no segment covers; none is shown without it. */
	remainderLabel?: string;
}

/**
 * A bar split into coloured parts by their share of a whole, with a legend beneath — the shape
 * a repository's language breakdown is drawn in. Whatever the parts leave of the whole stays the
 * bare track, so a meter that is not full reads as not full.
 */
export function SegmentedMeter({ segments, total, remainderLabel }: SegmentedMeterProps) {
	/** Computed */
	const percentOf = (value: number) => (total ? Math.max((value / total) * 100, 0) : 0);
	const covered = segments.reduce((sum, segment) => sum + percentOf(segment.value), 0);
	const remainder = Math.max(100 - covered, 0);

	/** Render */
	return (
		<div className="v-stack gap-2">
			<div className="h-stack gap-0.5 h-2 w-full rounded-full overflow-hidden bg-primary-100 dark:bg-primary-700">
				{segments.map((segment) => (
					<div
						key={segment.id}
						className="h-full first:rounded-l-full last:rounded-r-full"
						style={{
							width: `${percentOf(segment.value)}%`,
							backgroundColor: `var(--color-${segment.color}-500)`,
						}}
					/>
				))}
			</div>
			<div className="h-stack flex-wrap gap-x-4 gap-y-1">
				{segments.map((segment) => (
					<SegmentedMeterLegendItem
						key={segment.id}
						label={segment.label}
						color={segment.color}
						percent={percentOf(segment.value)}
					/>
				))}
				{remainderLabel && remainder > 0.05 && (
					<SegmentedMeterLegendItem
						label={remainderLabel}
						color="gray"
						percent={remainder}
					/>
				)}
			</div>
		</div>
	);
}
